/**
 * Editing rules: which connections are allowed, and what a block's lock covers.
 *
 * The server enforces the same rules on write — the design schema, the graph
 * read model and the block lock (backend/fluidea/core/graph.py and
 * core/blocks/lifecycle.py). They are repeated here so the editor can refuse
 * with a reason at the moment of the gesture, and highlight only valid targets
 * while connecting (D21), rather than letting a save fail later. The server
 * stays authoritative: anything this misses is still rejected on write.
 */
import type { DesignDocument, GroupInstanceDocument, NodeDocument, PortDocument } from "../design/document";
import type { SceneLibrary } from "../scene/types";
import type { Refusal } from "./state";

function topSegment(ref: string): string {
  const dot = ref.indexOf(".");
  return dot === -1 ? ref : ref.slice(0, dot);
}

export function findNode(design: DesignDocument, localId: string): NodeDocument | undefined {
  return design.nodes?.find((node) => node.local_id === localId);
}

export function findPort(
  design: DesignDocument,
  ref: string,
): { node: NodeDocument; port: PortDocument } | undefined {
  const node = findNode(design, topSegment(ref));
  const portId = ref.slice(topSegment(ref).length + 1);
  const port = node?.ports?.find((candidate) => candidate.local_id === portId);
  return node && port ? { node, port } : undefined;
}

/** The live (non-detached) block instance an element is locked into, if any. */
export function lockingInstance(design: DesignDocument, localId: string): GroupInstanceDocument | undefined {
  return design.group_instances?.find(
    (instance) => !instance.detached && (instance.member_local_ids ?? []).includes(localId),
  );
}

/**
 * The member port refs a block instance exposes, in design identifiers.
 *
 * Exposed ports are declared in the definition's own identifiers
 * (`valve_1.port_a`); members carry fresh ones (`valve_7`), so each is
 * translated through the instance's recorded origins.
 */
export function exposedPorts(instance: GroupInstanceDocument, library: SceneLibrary): Set<string> {
  const definition = library.definitions[instance.group_definition_version_id];
  const expansion = new Map(
    Object.entries(instance.member_origins ?? {}).map(([member, origin]) => [origin, member]),
  );
  const exposed = new Set<string>();
  for (const port of definition?.content.exposed_ports ?? []) {
    const origin = topSegment(port.internal_ref);
    const member = expansion.get(origin);
    if (member !== undefined) {
      exposed.add(member + port.internal_ref.slice(origin.length));
    }
  }
  return exposed;
}

/** The edge already using a port, if any, optionally not counting one edge. */
export function occupant(design: DesignDocument, ref: string, ignoring?: string): string | undefined {
  return design.edges?.find(
    (edge) => edge.local_id !== ignoring && (edge.from_port === ref || edge.to_port === ref),
  )?.local_id;
}

function refuse(code: Refusal["code"], message: string, refs: string[]): Refusal {
  return { code, message, refs };
}

/**
 * Whether a connection may start at `ref`. Checked on its own so the
 * connector tool can refuse the gesture immediately rather than at the drop.
 */
export function checkEndpoint(
  design: DesignDocument,
  library: SceneLibrary,
  ref: string,
  ignoring?: string,
): Refusal | null {
  const found = findPort(design, ref);
  if (!found) {
    return refuse("unknown_port", `${ref} is not a port in this design`, [ref]);
  }
  if (found.port.dead_end) {
    return refuse("dead_end_port", `${ref} is dead-ended; open it before connecting to it`, [ref]);
  }
  if (found.node.mount && (found.port.kind ?? "fluidic") === "fluidic") {
    // D40: the tube runs through a mounted part; it takes no line of its own.
    return refuse(
      "mounted_port",
      `${found.node.local_id} is mounted on ${found.node.mount.edge}; the tube runs through it`,
      [ref, found.node.mount.edge],
    );
  }
  const taken = occupant(design, ref, ignoring);
  if (taken !== undefined) {
    // Requirement 6.3: a port carries one connection. A junction is how a
    // channel branches, so the refusal says so.
    return refuse(
      "port_occupied",
      `${ref} is already connected by ${taken}; use a junction to branch`,
      [ref, taken],
    );
  }
  const instance = lockingInstance(design, found.node.local_id);
  if (instance && !exposedPorts(instance, library).has(ref)) {
    // D20: outside edges may use only the ports a block exposes, or the
    // collapsed symbol could not show the connection.
    return refuse(
      "unexposed_block_port",
      `${ref} is inside ${instance.local_id} and is not one of its exposed ports`,
      [ref, instance.local_id],
    );
  }
  return null;
}

/** Whether a connection from `from` to `to` is allowed. Null when it is. */
export function checkConnection(
  design: DesignDocument,
  library: SceneLibrary,
  from: string,
  to: string,
): Refusal | null {
  const start = checkEndpoint(design, library, from);
  if (start) return start;
  if (topSegment(from) === topSegment(to)) {
    // Requirement 6.4, and the schema's own rule for an edge to itself.
    return refuse("same_node", `${from} and ${to} are on the same component`, [from, to]);
  }
  return checkEndpoint(design, library, to);
}

export type EdgeEnd = "from" | "to";

/**
 * Whether one end of an existing edge may move to `port`. Null when it may.
 *
 * The same rules as making the connection, with the edge itself not counted
 * as occupying anything. An edge inside a locked block cannot be moved at
 * all: the lock covers how members connect to each other (D20).
 */
export function checkReconnect(
  design: DesignDocument,
  library: SceneLibrary,
  edgeRef: string,
  end: EdgeEnd,
  port: string,
): Refusal | null {
  const edge = design.edges?.find((candidate) => candidate.local_id === edgeRef);
  if (!edge) return refuse("unknown_element", `${edgeRef} is not a channel in this design`, [edgeRef]);
  const instance = lockingInstance(design, edgeRef);
  if (instance) {
    return refuse(
      "locked_block_member",
      `${edgeRef} is locked in ${instance.local_id}; detach it to rewire its members`,
      [edgeRef, instance.local_id],
    );
  }
  const other = end === "from" ? edge.to_port : edge.from_port;
  if (topSegment(other) === topSegment(port)) {
    return refuse("same_node", `${port} and ${other} are on the same component`, [port, other]);
  }
  return checkEndpoint(design, library, port, edgeRef);
}

/** Every port one end of an edge may move to, excluding where it already is. */
export function reconnectTargets(
  design: DesignDocument,
  library: SceneLibrary,
  edgeRef: string,
  end: EdgeEnd,
): Set<string> {
  const edge = design.edges?.find((candidate) => candidate.local_id === edgeRef);
  const current = edge ? (end === "from" ? edge.from_port : edge.to_port) : null;
  const targets = new Set<string>();
  for (const node of design.nodes ?? []) {
    for (const port of node.ports ?? []) {
      const ref = `${node.local_id}.${port.local_id}`;
      if (ref !== current && checkReconnect(design, library, edgeRef, end, ref) === null) targets.add(ref);
    }
  }
  return targets;
}

/**
 * The connection on `port`, and which end of it sits there. Null when the
 * port is free.
 */
export function connectionAt(design: DesignDocument, port: string): { edge: string; end: EdgeEnd } | null {
  const edge = design.edges?.find((candidate) => candidate.from_port === port || candidate.to_port === port);
  if (!edge) return null;
  return { edge: edge.local_id, end: edge.from_port === port ? "from" : "to" };
}

/**
 * The next port on the same component, in the component's own port order and
 * wrapping round, that the connection on `port` could move to. Null when there
 * is none. This is what Tab steps through when a port is highlighted.
 */
export function nextFreePort(
  design: DesignDocument,
  library: SceneLibrary,
  port: string,
  step: 1 | -1 = 1,
): string | null {
  const connection = connectionAt(design, port);
  const found = findPort(design, port);
  if (!connection || !found) return null;
  const ports = (found.node.ports ?? []).map((candidate) => `${found.node.local_id}.${candidate.local_id}`);
  const at = ports.indexOf(port);
  for (let offset = 1; offset < ports.length; offset += 1) {
    const candidate = ports[(at + step * offset + ports.length * offset) % ports.length]!;
    if (checkReconnect(design, library, connection.edge, connection.end, candidate) === null) return candidate;
  }
  return null;
}

/** Every port a connection from `from` may end on. */
export function validTargets(design: DesignDocument, library: SceneLibrary, from: string): Set<string> {
  const targets = new Set<string>();
  for (const node of design.nodes ?? []) {
    for (const port of node.ports ?? []) {
      const ref = `${node.local_id}.${port.local_id}`;
      if (checkConnection(design, library, from, ref) === null) {
        targets.add(ref);
      }
    }
  }
  return targets;
}

/**
 * Whether a component can be changed to another template in place: a flow
 * sensor into a bubble sensor, say. Only within one role, because a role is
 * where behaviour branches (D24) and the identifier's stem comes from it
 * (invariant 2); and only if every port a line uses is on the new template,
 * so no connection is lost. Null when it can.
 */
export function checkTemplateSwap(
  design: DesignDocument,
  library: SceneLibrary,
  ref: string,
  templateVersionId: string,
): Refusal | null {
  const node = findNode(design, ref);
  const template = library.templates[templateVersionId];
  if (!node) return refuse("unknown_element", `${ref} is not a component in this design`, [ref]);
  if (!template || (template.element ?? "node") !== "node") {
    return refuse("unknown_template", `template version ${templateVersionId} is not loaded`, [ref]);
  }
  const instance = lockingInstance(design, ref);
  if (instance) {
    return refuse("locked_block_member", `${ref} is locked in ${instance.local_id}; detach it to change its type`, [
      ref,
      instance.local_id,
    ]);
  }
  if (template.role !== node.role) {
    return refuse(
      "incompatible_template",
      `${ref} is a ${node.role.replace(/_/g, " ")}; it can change only to another of those`,
      [ref],
    );
  }
  const offered = new Set((template.ports ?? []).map((port) => port.local_id));
  const stranded = (node.ports ?? [])
    .filter((port) => !offered.has(port.local_id) && occupant(design, `${ref}.${port.local_id}`) !== undefined)
    .map((port) => port.local_id);
  if (stranded.length > 0) {
    return refuse(
      "incompatible_template",
      `it has no ${stranded.join(" or ")} port, which ${stranded.length === 1 ? "a line uses" : "lines use"}`,
      [ref],
    );
  }
  return null;
}
